import { Component } from '@verndale/core';
import { open, close } from '../helpers';

// tabbed-content module suports vertical orientation only.
const KEYS = {
  end: 35,
  home: 36,
  up: 38,
  down: 40
};

// Add or substract depending on key pressed
const DIRECTION = {
  38: -1,
  40: 1
};

class TabbedContent extends Component {
  setupDefaults() {
    this.dom = {
      tabList: this.el.querySelector('.tab-list'),
      inlineTabs: Array.from(this.el.querySelectorAll('.tab--inline')),
      externalTabs: Array.from(this.el.querySelectorAll('.tab--external')),
      sections: this.el.querySelector('.tab-sections'),
      panels: Array.from(this.el.querySelectorAll('.tab-section__panel')),
      rifCtas: Array.from(this.el.querySelectorAll('.tab-section__rif .btn')),
      rifModule: document.querySelector('.rif')
    };

    this.externalTabsIds = this.dom.externalTabs.map(tab => tab.id);

    this.activeIndex = this.setSelectedPanel();

    if (this.activeIndex !== 0) {
      this.hidePanelFirstTime()
    }
    this.showPanel(this.activeIndex);

  }

  addListeners() {
    this.dom.inlineTabs.forEach((inlineTab, index) => {
      inlineTab.index = index;
      inlineTab.addEventListener('click', this.handleInlineTabClick.bind(this));
      inlineTab.addEventListener('keydown', this.handleInlineTabKeydown.bind(this));
    });

    this.dom.externalTabs.forEach((externalTab, index) => {
      externalTab.index = index;
      externalTab.addEventListener('click', this.handleExternalTabClick.bind(this));
      externalTab.addEventListener('keydown', this.handleExternalTabKeydown.bind(this));
    });
    if (this.dom.rifModule) {
      this.dom.rifCtas.forEach(rifCta => {
        rifCta.addEventListener('click', this.onRifCtaClick.bind(this));
      })
    }
  }

  setActivePanel(index) {
    const mobileBreakpoint = 740;
    const windowSize = window.innerWidth;
    const isMobile = windowSize < mobileBreakpoint;

    if (this.activeIndex === index && isMobile) {
      this.hidePanel(this.activeIndex);
      this.activeIndex = null;
      return;
    }

    if (this.activeIndex === null && isMobile) {
      this.activeIndex = index;
      this.showPanel(this.activeIndex, true);
      return;
    }

    if (this.activeIndex !== index) {
      this.hidePanel(this.activeIndex);
      this.activeIndex = index;
      this.showPanel(this.activeIndex, true);
    }
  }

  setSelectedPanel() {
    const hash = window.location.hash;
    const isHashOnExternalTab = this.externalTabsIds.includes(hash.replace('#', ''))
    let activeId;
    if (hash && isHashOnExternalTab) {
      const activeTab = this.dom.externalTabs.find(panel => panel.id === hash.replace('#', ''));
      activeId = this.dom.externalTabs.indexOf(activeTab);
      setTimeout(() => this.el.scrollIntoView({ behavior: 'smooth' }), 1000);
    } else {
      activeId = 0;
    }

    return activeId;
  }


  showPanel(index, newUrl) {
    const externalTab = this.dom.externalTabs[index];
    const inlineTab = this.dom.inlineTabs[index];
    const panel = this.dom.panels[index];

    inlineTab.setAttribute('aria-disabled', 'true');
    inlineTab.setAttribute('aria-expanded', 'true');
    externalTab.setAttribute('aria-selected', 'true');
    externalTab.removeAttribute('tabindex');

    panel.removeAttribute('hidden');
    open(panel, 0.4, 'easeOut');

    if (newUrl) {
      // Update the url with the fata-anchor value
      // eslint-disable-next-line no-undef
      const newUrl = new URL(window.location.href);
      newUrl.hash = externalTab.id;
      window.history.pushState({}, '', newUrl.toString());
    }
  }

  hidePanel(index) {
    const externalTab = this.dom.externalTabs[index];
    const inlineTab = this.dom.inlineTabs[index];
    const panel = this.dom.panels[index];

    inlineTab.removeAttribute('aria-disabled');
    inlineTab.setAttribute('aria-expanded', 'false');
    externalTab.setAttribute('aria-selected', 'false');
    externalTab.setAttribute('tabindex', '-1');

    panel.setAttribute('hidden', 'hidden');
    close(panel, 0.4, 'easeOut');
  }

  hidePanelFirstTime() {
    const externalTab = this.dom.externalTabs[0];
    const inlineTab = this.dom.inlineTabs[0];
    const panel = this.dom.panels[0];

    inlineTab.removeAttribute('aria-disabled');
    inlineTab.setAttribute('aria-expanded', 'false');
    externalTab.setAttribute('aria-selected', 'false');
    externalTab.setAttribute('tabindex', '-1');

    panel.setAttribute('hidden', 'hidden');
    close(panel, 0.4, 'easeOut');
  }

  setActivePanelByKey(e, tabs) {
    const key = e.keyCode;

    switch (key) {
    case KEYS.end:
      e.preventDefault();
      // Activate last tab
      const lastIndex = tabs.length - 1;
      tabs[lastIndex].focus();
      break;

    case KEYS.home:
      e.preventDefault();
      // Activate first tab
      tabs[0].focus();
      break;

    case KEYS.up:
    case KEYS.down:
      e.preventDefault();
      // Up and down are in keydown
      // because we need to prevent page scroll
      const externalTab = e.target;
      let index = externalTab.index + DIRECTION[key];
      if (!tabs[index]) {
        if (key === KEYS.up) {
          index = tabs.length - 1;
        } else if (key === KEYS.down) {
          index = 0;
        }
      }
      tabs[index].focus();
      break;
    }
  }

  handleInlineTabClick(e) {
    const index = this.dom.inlineTabs.indexOf(e.currentTarget);
    this.setActivePanel(index);
  }

  handleExternalTabClick(e) {
    const index = this.dom.externalTabs.indexOf(e.currentTarget);
    this.setActivePanel(index);
  }

  handleInlineTabKeydown(e) {
    this.setActivePanelByKey(e, this.dom.inlineTabs);
  }

  handleExternalTabKeydown(e) {
    this.setActivePanelByKey(e, this.dom.externalTabs);
  }

  onRifCtaClick(e) {
    e.preventDefault();
    const rifTop = this.dom.rifModule.offsetTop - 100;
    const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;
    if (!supportsNativeSmoothScroll) {
      return window.scrollTo(0, rifTop);
    }
    window.scrollTo({
      top: rifTop,
      left: 0,
      behavior: 'smooth'
    });
  }
}

export default TabbedContent;
